import { ComunicazioneDTO, TipoComunicazioneDTO } from '@framework/api-client';
import {
  DateInput,
  FormHandler,
  FormItem,
  SelectInput,
  SelectOption,
  SubmitButton,
  TextAreaInput,
  TextInput,
} from '@framework/form';
import { useTranslation } from '@framework/i18n';
import {
  DividerTitleElement,
  DrawerButtonRow,
  FlexRow,
  RowElement,
} from '@framework/ui';
import { AnyObject, InferType, ObjectSchema } from '@framework/validation';
import { Button, message, Space } from 'antd';
import dayjs from 'dayjs';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useCommunicationsContext } from '../context/CommunicationsContext';
import { ICommonCommunicationProps } from '../logic/ICommonCommunicationProps';
import { CommunicationAttachmentFields } from './CommunicationAttachmentFields';
import { CommunicationSendButton } from './CommunicationSendButton';
import { CommunicationTypeFields } from './CommunicationTypeFields';

export interface CommunicationFormProps<
  T extends AnyObject,
  TSchema extends ObjectSchema<T> = ObjectSchema<T>,
> extends Omit<ICommonCommunicationProps<T, TSchema>, 'deleteDraft'> {
  communication?: ComunicazioneDTO | null;
  validationSchema: TSchema;
  onClose?: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  taskId?: string;
}

/**
 * Form di creazione di una comunicazione
 */
export function CommunicationForm<
  T extends AnyObject,
  TSchema extends ObjectSchema<T>,
>(props: CommunicationFormProps<T, TSchema>) {
  const { t } = useTranslation();
  const {
    communication,
    validationSchema,
    sendCommunication,
    onClose,
    onSubmit,
    taskId,
  } = props;

  const [type, setType] = useState<TipoComunicazioneDTO | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const { sendableTypes, receivableTypes } = useCommunicationsContext();

  // Le tipologie di comunicazione sono diverse a seconda che si tratti di
  // una comunicazione ricevuta o inviata.
  // Quando si carica manualmente una comunicazione, si possono selezionare
  // solo quelle _Ricevibili_, perché significa che l'utente X ha ricevuto
  // una comunicazione da Y e la sta caricando manualmente sul sistema (in
  // quanto _destinatario_).
  const availableTypes = communication?.flCaricataManualmente
    ? receivableTypes!
    : sendableTypes!;
  const typeOptions: SelectOption[] = availableTypes
    .filter(({ codice, id }) => !!codice && !!id)
    .map(type => ({
      label: type.codice!,
      value: type.id!,
    }));

  const handleTypeChange = (value?: string) => {
    setType(availableTypes.find(type => type.id === value) ?? undefined);
  };

  const selectedType = type ?? communication?.tipoComunicazione;

  const isNew = communication?.id == null;
  const isManual = !!communication?.flCaricataManualmente;

  const initialValues = useMemo(() => {
    return communication
      ? validationSchema.cast(
          {
            // L'ufficio destinatario non ci viene ritornato nello
            // stesso campo, provvediamo a rimapparlo.
            idDestinatarioEndoprocedimentoPrimoLivello:
              communication.destinatariProcedimenti?.[0]?.idProcedimento,
            // Stesso discorso per l'ufficio mittente.
            // Potrebbe essere già impostato dalla comunicazione ricevuta,
            // in caso di creazione, quindi è prima dello spread.
            idProcedimentoMittente:
              communication.procedimentoMittente?.idProcedimento,

            // Dati della comunicazione
            ...communication,

            // Dati di protocollo
            numeroProtocollo:
              communication.destinatariProcedimenti?.[0]?.numeroProtocollo,
            dataProtocollo: communication.dataInvio,
          },
          { assert: false },
        )
      : validationSchema.cast({}, { assert: false });
  }, [communication, validationSchema]);

  const validationContext = useMemo(() => {
    return {
      isEdit: communication?.id != null,
    };
  }, [communication]);

  const handleSubmit = useCallback(
    async (data: InferType<TSchema>) => {
      setIsSaving(true);
      await onSubmit?.(data);
      setIsSaving(false);
    },
    [onSubmit],
  );

  const handleSend = async () => {
    if (!communication) return;
    try {
      await sendCommunication?.(communication.id!);
      message.success(
        t(
          'communications.sent-successfully',
          'Comunicazione inviata con successo',
        ),
      );
      onClose?.();
    } catch (e) {
      message.error(
        t(
          'communications.error-sending-communication',
          "Si è verificato un errore durante l'invio della comunicazione",
        ),
      );
      console.error(e);
    }
  };

  const todayDate = useMemo(() => dayjs(), []);

  const attachments = communication?.allegatoComunicazioneDTO;

  return (
    <FormHandler
      id="communication"
      layout="vertical"
      nested
      schema={validationSchema as any}
      validationContext={validationContext}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <div>
        <DividerTitleElement
          title={t('communications.type-title', 'Tipo di Comunicazione')}
          removeTopMargin
        />

        {isNew ? (
          <FormItem
            name="idTipoComunicazione"
            label={t('communications.type-label', 'Tipo Comunicazione')}
          >
            <SelectInput
              name="idTipoComunicazione"
              options={typeOptions}
              placeholder={t('communications.select', 'Selezione...')}
              onChange={(value, option, onChange) => {
                handleTypeChange(value);
                onChange(value);
              }}
            />
          </FormItem>
        ) : (
          <FlexRow>
            <Space>
              <span>{t('communications.type-row', 'Tipo Comunicazione')}:</span>
              <strong>{communication.tipoComunicazione?.codice}</strong>
            </Space>
          </FlexRow>
        )}

        {selectedType && (
          <>
            <CommunicationTypeFields
              communication={communication}
              communicationType={selectedType}
            />
            <FormItem
              name="noteAddizionali"
              label={t('communications.additional-notes', 'Note addizionali')}
            >
              <TextAreaInput
                name="noteAddizionali"
                placeholder={t(
                  'communications.additional-notes-placeholder',
                  'Note addizionali alla comunicazione',
                )}
                rows={4}
              />
            </FormItem>
            <RowElement>
              <FormItem
                name="dataProtocollo"
                label={t('communications.protocol-date', 'Data Protocollo')}
              >
                <DateInput
                  showTime
                  type="datetime"
                  format="DD/MM/YYYY HH:mm:ss"
                  maxDate={todayDate}
                  name="dataProtocollo"
                />
              </FormItem>
              <FormItem
                name="numeroProtocollo"
                label={t('communications.protocol-number', 'Numero Protocollo')}
              >
                <TextInput name="numeroProtocollo" />
              </FormItem>
            </RowElement>

            <FormItem
              name="oggetto"
              label={t('communications.subject', 'Oggetto')}
            >
              <TextInput
                name="oggetto"
                placeholder={t(
                  'communications.subject-placeholder',
                  'Oggetto della comunicazione',
                )}
              />
            </FormItem>
            <FormItem
              name="messaggio"
              label={t('communications.message', 'Messaggio')}
            >
              <TextAreaInput
                name="messaggio"
                placeholder={t(
                  'communications.message-placeholder',
                  'Messaggio della comunicazione',
                )}
                rows={4}
              />
            </FormItem>

            <CommunicationAttachmentFields
              communication={communication}
              taskId={taskId}
              communicationType={selectedType}
              uploadAttachment={props.uploadAttachment}
              removeAttachment={props.removeAttachment}
              attachExisting={props.attachExisting}
            />
          </>
        )}
        <DrawerButtonRow>
          <Space>
            <Button
              size="large"
              onClick={e => {
                onClose?.(e);
              }}
            >
              {t('interazioni.annulla', 'Annulla')}
            </Button>
            <SubmitButton size="large" type="primary" loading={isSaving}>
              {t('interazioni.salva', 'Salva')}
            </SubmitButton>
            {!isManual && <CommunicationSendButton onSend={handleSend} />}
          </Space>
        </DrawerButtonRow>
      </div>
    </FormHandler>
  );
}
